/**
 * useChartOfAccounts
 *
 * Orchestrates state and data for the Chart of Accounts page.
 * Composes useAccountTree (data/tree) and useAccountCrud (CRUD/form handlers).
 */

import { useState, useMemo, useCallback, useEffect } from 'react'

import { getReservedAccounts } from '../../services/reserved-account-service'

import {
  EMPTY_FORM_DATA,
  sortIdsByCode,
  type AccountFormData,
  type AccountRow,
} from './chart-of-accounts-helpers'
import { useAccountTree } from './useAccountTree'
import {
  doCreateAccount,
  doUpdateAccount,
  saveBankDetails,
} from './useChartOfAccounts.helpers'
import { useToast } from '../../components/ui'
import { isCashOrBankSubtype } from '../../constants/labels'
import { type ReservedAccountCode } from '../../constants/reserved-account-codes'
import { useCurrencies, useEnabledCurrencies } from '../../hooks/use-currencies'
import {
  deactivateAccount,
  reactivateAccount,
  type Account,
  type AccountType,
} from '../../services/account-service'
import { listBankAccounts } from '../../services/bank-account-service'
import { useLedgerContext } from '../../stores/ledger-store'

import type { FilterBarCondition } from '@elsapiens/ui'


export interface UseChartOfAccountsReturn {
  organizationId: string
  accountsMap: Map<string, Account>
  rootAccountsByType: Map<AccountType, string[]>
  childrenMap: Map<string, string[]>
  rowsByType: Map<AccountType, AccountRow[]>
  parentOptions: Array<{ value: string; label: string }>
  subtypeOptions: Array<{ value: string; label: string }>
  currencyOptions: Array<{ value: string; label: string }>
  CURRENCIES: Array<{ value: string; label: string }>
  enabledCurrencies: Array<{ value: string; label: string }>
  baseCurrency: string
  isLoading: boolean
  loadingChildren: Set<string>
  isLoadingBankDetails: boolean
  expandedTypes: Set<string>
  expandedAccounts: Set<string>
  toggleTypeExpanded: (type: string) => void
  toggleAccountExpanded: (accountId: string) => Promise<void>
  searchQuery: string
  setSearchQuery: (q: string) => void
  filterConditions: FilterBarCondition[]
  setFilterConditions: (conds: FilterBarCondition[]) => void
  activeTab: string
  setActiveTab: (tab: string) => void
  sortField: string
  setSortField: (f: string) => void
  sortDirection: 'asc' | 'desc'
  setSortDirection: (d: 'asc' | 'desc') => void
  isModalOpen: boolean
  setIsModalOpen: (open: boolean) => void
  editingAccount: Account | null
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  isSaving: boolean
  tagInput: string
  setTagInput: (v: string) => void
  isShowCurrencyConfirm: boolean
  setIsShowCurrencyConfirm: (v: boolean) => void
  handleOpenCreate: () => void
  handleOpenCreateReserved: (reserved: ReservedAccountCode) => void
  handleOpenEdit: (account: Account) => Promise<void>
  handleSave: () => Promise<void>
  handleToggleActive: (account: Account) => Promise<void>
  fetchRootAccounts: () => Promise<void>
  reservedAccounts: ReservedAccountCode[]
  /** Root-fetch pagination status, for the CursorRangeText status caption (see AccountTree/ChartOfAccountsPage). */
  loadedCount: number
  remainingCount: number
  remainingIsExact: boolean
}

// eslint-disable-next-line max-lines-per-function -- aggregator hook; sub-effects/handlers/helpers already extracted
export function useChartOfAccounts(): UseChartOfAccountsReturn {
  const { addToast } = useToast()
  const { currencies: CURRENCIES } = useCurrencies()
  const { currencies: enabledCurrencies } = useEnabledCurrencies()
  const { currentLedgerId } = useLedgerContext()

  const [searchQuery, setSearchQuery] = useState('')
  const [filterConditions, setFilterConditions] = useState<FilterBarCondition[]>([
    { id: 'tab-status', fieldId: 'status', operator: 'equals', value: 'active' },
  ])
  const [activeTab, setActiveTab] = useState('active')
  const [sortField, setSortField] = useState('code')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingAccount, setEditingAccount] = useState<Account | null>(null)
  const [formData, setFormData] = useState<AccountFormData>(EMPTY_FORM_DATA)
  const [isSaving, setIsSaving] = useState(false)
  const [reservedAccounts, setReservedAccounts] = useState<ReservedAccountCode[]>([])
  const [isLoadingBankDetails, setIsLoadingBankDetails] = useState(false)
  const [tagInput, setTagInput] = useState('')
  const [isShowCurrencyConfirm, setIsShowCurrencyConfirm] = useState(false)

  // Load the authoritative reserved-account catalog (single source of truth).
  useEffect(() => {
    let active = true
    void getReservedAccounts().then(list => { if (active) { setReservedAccounts(list) } }).catch(() => {})
    return () => { active = false }
  }, [])

  const tree = useAccountTree(searchQuery, filterConditions, sortField, sortDirection)

  const parentOptions = useMemo(() => {
    const options = Array.from(tree.accountsMap.values())
      .filter(a => a.accountType === formData.accountType && a.isHeader && a.id !== editingAccount?.id)
      .map(a => ({ value: a.id, label: `${'-'.repeat(a.depth)} ${a.code} - ${a.name}` }))
    return [{ value: '', label: 'No parent (root level)' }, ...options]
  }, [tree.accountsMap, formData.accountType, editingAccount])

  const subtypeOptions = useMemo(() => {
    const subtypes = new Set<string>()
    tree.accountsMap.forEach(a => { if (a.accountSubtype) { subtypes.add(a.accountSubtype) } })
    return [...subtypes].sort().map(s => ({ value: s, label: s }))
  }, [tree.accountsMap])

  const currencyOptions = useMemo(() => {
    const currencies = new Set<string>()
    tree.accountsMap.forEach(a => currencies.add(a.currency ?? tree.baseCurrency))
    return [...currencies].sort().map(c => ({ value: c, label: c }))
  }, [tree.accountsMap, tree.baseCurrency])

  const handleOpenCreate = useCallback((): void => {
    setEditingAccount(null)
    setFormData({ ...EMPTY_FORM_DATA, currency: tree.baseCurrency })
    setTagInput('')
    setIsModalOpen(true)
  }, [tree.baseCurrency])

  const handleOpenCreateReserved = useCallback((reserved: ReservedAccountCode): void => {
    setEditingAccount(null)
    const parentAccount = Array.from(tree.accountsMap.values()).find(a => a.code === reserved.parentCode)
    setFormData({
      ...EMPTY_FORM_DATA,
      currency: tree.baseCurrency,
      code: reserved.code,
      name: reserved.defaultName,
      accountType: reserved.accountType,
      accountSubtype: reserved.accountSubtype,
      parentId: parentAccount?.id ?? '',
      systemTag: reserved.systemTag ?? '', // stamp the backend reserved tag on create
    })
    setTagInput('')
    setIsModalOpen(true)
  }, [tree.baseCurrency, tree.accountsMap])

  const handleOpenEdit = useCallback(async (account: Account): Promise<void> => {
    setEditingAccount(account)
    setFormData({
      code: account.code, name: account.name, description: account.description,
      accountType: account.accountType, accountSubtype: account.accountSubtype ?? '',
      parentId: account.parentId ?? '', isHeader: account.isHeader,
      currency: account.currency ?? tree.baseCurrency, tags: account.tags ?? [],
      nameAtBank: '', bankName: '', branch: '', accountNumber: '',
      ifscCode: '', swiftCode: '', iban: '', routingNumber: '', sortCode: '',
      systemTag: account.systemTag ?? '',
      gstComponent: account.gstComponent ?? '',
      gstDirection: account.gstDirection ?? '',
      gstRate: account.gstRate != null ? String(account.gstRate) : '',
    })
    setTagInput('')
    setIsModalOpen(true)
    if (isCashOrBankSubtype(account.accountSubtype) && currentLedgerId) {
      setIsLoadingBankDetails(true)
      try {
        const response = await listBankAccounts(tree.organizationId, { ledgerId: currentLedgerId, activeOnly: false })
        const linked = (response.data?.bankAccounts ?? []).find(ba => ba.accountId === account.id)
        if (linked) {
          setFormData(prev => ({
            ...prev, nameAtBank: linked.nameAtBank ?? '', bankName: linked.bankName ?? '',
            branch: linked.branch ?? '', accountNumber: linked.accountNumber ?? '',
            ifscCode: linked.ifscCode ?? '', swiftCode: linked.swiftCode ?? '',
            iban: linked.iban ?? '', routingNumber: linked.routingNumber ?? '', sortCode: linked.sortCode ?? '',
          }))
        }
      } catch { /* non-critical */ } finally { setIsLoadingBankDetails(false) }
    }
  }, [tree.baseCurrency, tree.organizationId, currentLedgerId])

  async function saveBankIfNeeded(coaAccountId: string): Promise<void> {
    if (!currentLedgerId) {return}
    await saveBankDetails({
      organizationId: tree.organizationId,
      ledgerId: currentLedgerId,
      baseCurrency: tree.baseCurrency,
      coaAccountId,
      formData,
      addToast,
    })
  }

  async function doUpdate(account: Account): Promise<void> {
    const updated = await doUpdateAccount({
      organizationId: tree.organizationId, account, formData, addToast,
    })
    if (!updated) {return}
    if (isCashOrBankSubtype(formData.accountSubtype)) {await saveBankIfNeeded(account.id)}
    const newParentId = formData.parentId !== '' ? formData.parentId : undefined
    const updatedAccount: Account = {
      ...account,
      code: formData.code || account.code,
      name: formData.name,
      description: formData.description,
      parentId: newParentId,
      tags: formData.tags,
    }
    tree.setAccountsMap(prev => { const next = new Map(prev); next.set(account.id, updatedAccount); return next })

    if (newParentId !== account.parentId) {
      const getCode = (id: string): string => (id === account.id ? updatedAccount : tree.accountsMap.get(id))?.code ?? ''
      // Remove from old location
      if (account.parentId) {
        tree.setChildrenMap(prev => {
          const next = new Map(prev)
          const siblings = (next.get(account.parentId!) ?? []).filter(id => id !== account.id)
          next.set(account.parentId!, siblings)
          return next
        })
      } else {
        tree.setRootAccountsByType(prev => {
          const next = new Map(prev)
          const roots = (next.get(account.accountType) ?? []).filter(id => id !== account.id)
          next.set(account.accountType, roots)
          return next
        })
      }
      // Insert into new location
      if (newParentId) {
        tree.setChildrenMap(prev => {
          const next = new Map(prev)
          const siblings = next.get(newParentId) ?? []
          next.set(newParentId, sortIdsByCode([...siblings, account.id], getCode))
          return next
        })
      } else {
        tree.setRootAccountsByType(prev => {
          const next = new Map(prev)
          const roots = next.get(account.accountType) ?? []
          next.set(account.accountType, sortIdsByCode([...roots, account.id], getCode))
          return next
        })
      }
    }
    addToast({ variant: 'success', message: `${formData.name} has been updated.` })
  }

  async function doCreate(): Promise<void> {
    const isBankCash = isCashOrBankSubtype(formData.accountSubtype)
    const newAccount = await doCreateAccount({
      organizationId: tree.organizationId, formData, isBankCash, addToast,
    })
    if (!newAccount) {return}
    if (isBankCash) {await saveBankIfNeeded(newAccount.id)}
    insertNewAccount(newAccount)
    addToast({ variant: 'success', message: `${formData.name} has been created.` })
  }

  function insertNewAccount(newAccount: Account): void {
    tree.setAccountsMap(prev => { const next = new Map(prev); next.set(newAccount.id, newAccount); return next })
    const getCode = (id: string): string => (tree.accountsMap.get(id) ?? (id === newAccount.id ? newAccount : undefined))?.code ?? ''
    if (newAccount.parentId) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- TODO(refactor): replace with proper null guard
      tree.setChildrenMap(prev => { const next = new Map(prev); const siblings = next.get(newAccount.parentId!) ?? []; next.set(newAccount.parentId!, sortIdsByCode([...siblings, newAccount.id], getCode)); return next })
    } else {
      tree.setRootAccountsByType(prev => { const next = new Map(prev); const roots = next.get(newAccount.accountType) ?? []; next.set(newAccount.accountType, sortIdsByCode([...roots, newAccount.id], getCode)); return next })
    }
  }

  async function handleSave(): Promise<void> {
    if (!editingAccount && enabledCurrencies.length > 1 && !isShowCurrencyConfirm) { setIsShowCurrencyConfirm(true); return }
    setIsShowCurrencyConfirm(false)
    setIsSaving(true)
    try {
      if (editingAccount) { await doUpdate(editingAccount) } else { await doCreate() }
      setIsModalOpen(false)
    } catch {
      addToast({ variant: 'error', message: editingAccount ? 'Failed to update account. Please check your input.' : 'Failed to create account. Please check your input.' })
    } finally { setIsSaving(false) }
  }

  const handleToggleActive = useCallback(async (account: Account): Promise<void> => {
    try {
      if (account.isActive) {
        const resp = await deactivateAccount(tree.organizationId, account.id)
        if (resp.error) { throw new Error(resp.error.message ?? 'Failed to deactivate account') }
        addToast({ variant: 'success', message: `${account.name} has been deactivated.` })
      } else {
        const resp = await reactivateAccount(tree.organizationId, account.id)
        if (resp.error) { throw new Error(resp.error.message ?? 'Failed to reactivate account') }
        addToast({ variant: 'success', message: `${account.name} has been reactivated.` })
      }
      void tree.fetchRootAccounts()
    } catch (error) {
      addToast({ variant: 'error', message: error instanceof Error ? error.message : 'Action failed. Please try again later.' })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO(refactor): verify dependency array intentional
  }, [tree.organizationId, addToast, tree.fetchRootAccounts])

  return {
    organizationId: tree.organizationId,
    accountsMap: tree.accountsMap, rootAccountsByType: tree.rootAccountsByType,
    childrenMap: tree.childrenMap, rowsByType: tree.rowsByType,
    parentOptions, subtypeOptions, currencyOptions,
    CURRENCIES, enabledCurrencies, baseCurrency: tree.baseCurrency,
    isLoading: tree.isLoading, loadingChildren: tree.loadingChildren, isLoadingBankDetails,
    expandedTypes: tree.expandedTypes, expandedAccounts: tree.expandedAccounts,
    toggleTypeExpanded: tree.toggleTypeExpanded, toggleAccountExpanded: tree.toggleAccountExpanded,
    searchQuery, setSearchQuery, filterConditions, setFilterConditions,
    activeTab, setActiveTab, sortField, setSortField, sortDirection, setSortDirection,
    isModalOpen, setIsModalOpen, editingAccount, formData, setFormData,
    isSaving, tagInput, setTagInput, isShowCurrencyConfirm, setIsShowCurrencyConfirm,
    handleOpenCreate, handleOpenCreateReserved, handleOpenEdit, handleSave, handleToggleActive,
    fetchRootAccounts: tree.fetchRootAccounts,
    reservedAccounts,
    loadedCount: tree.loadedCount, remainingCount: tree.remainingCount, remainingIsExact: tree.remainingIsExact,
  }
}
